import React, { ReactElement, useState } from "react";

import { FaCheck } from "react-icons/fa";

import { useDisclosure } from "@chakra-ui/react";
import { Spacer } from "@chakra-ui/react";
import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  VStack,
  List,
  ListItem,
  ListIcon,
  Text,
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  HStack,
  Divider,
} from "@chakra-ui/react";

import { PlanEnum } from "@/__generated__/graphql";
import { planItems } from "@/components/WorkspaceSubscription/planItems";
import ResourceCalculator from "@/components/WorkspaceSubscription/ResourceCalculator";
import { Service } from "@/components/WorkspaceSubscription/types";
import { getPlanDisplayName } from "@/components/WorkspaceSubscription/utils";

type Props = {
  plan: PlanEnum;
  action?: ReactElement;
  showFreeTrial?: boolean;
};

export default function PricingCard({ plan, action, showFreeTrial }: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [estimatedCost, setEstimatedCost] = useState<number | null>(null);
  const [costServices, setCostServices] = useState<Service[] | null>(null);

  const handleCostChange = (cost: number, services: Service[]) => {
    setEstimatedCost(cost);
    setCostServices(services);
  };

  const { card, description, headerText, external_features } = planItems[plan];

  // Add type guard for external_features
  const hasExclusiveFeatures =
    external_features && Array.isArray(external_features.exclusive) && external_features.exclusive.length > 0;

  // Get background color based on plan
  let planBackgroundAttrs = {};
  switch (plan) {
    case PlanEnum.Enterprise: {
      planBackgroundAttrs = { background: "plum.500" };
      break;
    }
    case PlanEnum.Teams: {
      planBackgroundAttrs = { background: "plum.500" };
      break;
    }
    case PlanEnum.Free: {
      planBackgroundAttrs = { background: "niagara.600" };
      break;
    }
    case PlanEnum.Sandbox: {
      planBackgroundAttrs = { background: "gray.500" };
      break;
    }
  }

  const renderCostBreakdown = () => {
    if (!costServices || !estimatedCost) return null;

    return (
      <VStack
        align="start"
        bg="rgba(255, 255, 255, 0.05)" // Very subtle light background
        border="1px solid"
        borderColor="whiteAlpha.100"
        borderRadius="md"
        mt={4}
        p={4}
        spacing={2}
      >
        <Text color="white" fontWeight="medium">
          Estimated Cost Breakdown:
        </Text>
        {costServices.map(service => (
          <HStack key={service.id} fontSize="sm" justify="space-between" w="full">
            <Text color="gray.400">{service.name}:</Text>
            <Text color="gray.400">${service.gpu > 0 ? 2.0 : (service.cpu * 0.125).toFixed(2)}/hour</Text>
          </HStack>
        ))}
        <Divider borderColor="whiteAlpha.200" />
        <HStack justify="space-between" w="full">
          <Text color="white" fontWeight="medium">
            Total Estimated Cost:
          </Text>
          <Text color="white" fontSize="lg" fontWeight="bold">
            ${estimatedCost.toFixed(2)}/hour
          </Text>
        </HStack>
      </VStack>
    );
  };

  return (
    <Card
      bg="rgb(31 41 55)"
      border="1px solid"
      borderColor="rgba(255, 255, 255, 0.1)"
      borderRadius="xl"
      boxShadow="dark-lg"
      height="100%"
      maxW="100%"
      overflow="hidden"
      sx={{
        "--tw-bg-opacity": "1",
        backgroundColor: "rgb(31 41 55 / var(--tw-bg-opacity))",
      }}
    >
      <Heading
        as="h2"
        borderTopLeftRadius="md"
        borderTopRightRadius="md"
        color="white"
        p={3}
        size={{ base: "sm", lg: "md" }}
        variant="italic"
        {...planBackgroundAttrs}
      >
        {getPlanDisplayName(plan)}
      </Heading>
      <CardHeader border="none">
        <Heading color="white" size="sm">
          {headerText}
        </Heading>
        <Text color="gray.400" mt={4}>
          {description}
        </Text>
      </CardHeader>
      <CardBody>
        <VStack align="stretch" height="full" spacing={6}>
          {/* Resource Specifications */}
          <List spacing={3}>
            {card.map(({ icon, text, price }) => (
              <ListItem key={typeof text === "string" ? text : "custom"} color="gray.400">
                <ListIcon
                  as={icon}
                  boxSize={4}
                  color="niagara.500"
                  mt={1}
                  sx={{
                    path: {
                      stroke: "var(--chakra-colors-niagara-500) !important",
                    },
                  }}
                />
                <Text display="inline">{text}</Text>
                {price && (
                  <Text color="gray.400" mt={1} whiteSpace="pre-wrap">
                    {price}
                  </Text>
                )}
              </ListItem>
            ))}
          </List>

          {/* Plan-Specific Features */}
          {hasExclusiveFeatures && (
            <Box>
              <Text color="white" fontWeight="medium" mb={2}>
                {plan === PlanEnum.Free ? "Release Cloud Features" : "Enterprise Features"}
              </Text>
              <List spacing={2}>
                {external_features.exclusive.map(text => (
                  <ListItem key={text} color="gray.400">
                    <ListIcon as={FaCheck} boxSize={4} color="niagara.500" mt={1} />
                    <Text display="inline">{text}</Text>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Spacer />

          {plan === PlanEnum.Free && (
            <>
              {renderCostBreakdown()}
              <Button
                _hover={{
                  bg: "whiteAlpha.100",
                  borderColor: "white",
                }}
                bg="transparent"
                borderColor="whiteAlpha.400"
                color="white"
                size="lg"
                variant="outline"
                width="full"
                onClick={onOpen}
              >
                Estimate Cost
              </Button>
            </>
          )}

          {action && (
            <Box mt={0}>
              {React.cloneElement(action as ReactElement, {
                colorScheme: plan === PlanEnum.Free ? "niagara" : "plum",
                variant: "solid",
              })}
            </Box>
          )}
        </VStack>
      </CardBody>

      <Modal isOpen={isOpen} size="xl" onClose={onClose}>
        <ModalOverlay />
        <ModalContent bg="rgb(31 41 55)" color="white" maxW="800px">
          <ModalHeader color="white">Cost Calculator</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody pb={6}>
            <ResourceCalculator theme="dark" onCostChange={handleCostChange} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
}
